const MAX_ENTRIES = 6;
const MAX_VALUE_LENGTH = 80;
// documentId arguments (id, clip_id, planId…): the preview already names the
// target, so these only add noise next to it.
const ID_KEY = /(^id$|_id$|Id$)/;

const truncate = (text: string) => (text.length > MAX_VALUE_LENGTH
  ? `${text.slice(0, MAX_VALUE_LENGTH - 1)}…`
  : text);

function formatValue(value: unknown): string | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') return truncate(value);
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    const parts = value.map(formatValue).filter((part): part is string => part !== null);
    return parts.length ? truncate(parts.join(', ')) : null;
  }
  try {
    return truncate(JSON.stringify(value));
  } catch {
    return null;
  }
}

export interface ActionArgRow {
  key: string;
  value: string;
}

// The arguments of a proposed action as short key/value rows for the
// confirmation card, so the user sees exactly what will run.
export function summarizeActionArgs(
  args: unknown,
  { hideIds = false }: { hideIds?: boolean } = {},
): ActionArgRow[] {
  if (!args || typeof args !== 'object' || Array.isArray(args)) return [];
  return Object.entries(args as Record<string, unknown>)
    .filter(([key]) => !(hideIds && ID_KEY.test(key)))
    .map(([key, value]) => ({ key, value: formatValue(value) }))
    .filter((row): row is ActionArgRow => row.value !== null)
    .slice(0, MAX_ENTRIES);
}

// "aspect_ratio_format" → "Aspect ratio format": the label used when a key has
// no translation yet.
export function humanizeArgKey(key: string): string {
  const words = key
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : key;
}
